import cx from 'classnames';
import * as React from 'react';
import { useMonthRange } from '../hooks/use-month-range';
import { determinePrice, getNumberParts, parseNumber } from '../lib';
import { ServiceSchedule } from '../shared-types';
import { DateField } from './date-field';
import { useLocalize } from './localize-context';
import { MessageBtn } from './message-btn';
import { SelectField } from './select-field';
import { TextField } from './text-field';
import { VisitorContext } from './visitor-context';
import { VisitorTypeSelect } from './visitor-type-select';
import { trackEvent } from '../analytics';

type PackagesBookingFormProps = {
  packages: PackageDetails[];
  schedules: ServiceSchedule[];
};

export const PackagesBookingForm = (props: PackagesBookingFormProps) => {
  const l = useLocalize();
  const displayedPackages = React.useMemo(
    () =>
      props.packages.map(({ data, uid }) => ({
        id: uid,
        title: data.name.text,
        includes: data.includedServices
          .map(svc => {
            const serviceData = svc.service.document[0];
            return serviceData ? serviceData.data.name.text : '';
          })
          .concat(data.additionalServices.map(svc => svc.text.text))
          .filter(Boolean),
      })),
    [props.packages]
  );
  const [adultCount, setAdultCount] = React.useState('');
  const [kidCount, setKidCount] = React.useState('');
  const adultNum = parseNumber(adultCount);
  const kidNum = parseNumber(kidCount);
  const totalPeople = adultNum + kidNum;
  const [pkg, setPkg] = React.useState('');
  const selectedPackage = React.useMemo(
    () => props.packages.find(({ uid }) => uid === pkg),
    [props.packages, pkg]
  );
  const [visitorType] = React.useContext(VisitorContext);
  const [adultPrice, kidPrice] = React.useMemo<[number, number]>(() => {
    if (!selectedPackage) {
      return [0, 0];
    }
    const isForeigner = visitorType === 'foreigner';

    const adultPrice = isForeigner
      ? selectedPackage.data.foreignAdultPrice
      : selectedPackage.data.localAdultPrice;
    const kidPrice = isForeigner
      ? selectedPackage.data.foreignKidPrice
      : selectedPackage.data.localKidPrice;
    return selectedPackage.data.discounts
      ? determinePrice(
          selectedPackage.data.discounts.map(disc => ({
            requiredUnit: disc.requiredUnit,
            kidPrice: isForeigner
              ? disc.foreignAdultPrice
              : disc.localAdultPrice,
            adultPrice: isForeigner ? disc.foreignKidPrice : disc.localKidPrice,
          })),
          adultPrice,
          kidPrice,
          totalPeople
        )
      : [adultPrice, kidPrice];
  }, [selectedPackage, visitorType, totalPeople]);
  const [totalPrice, decimals] = getNumberParts(
    adultNum * adultPrice + kidNum * kidPrice
  );

  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('');
  const allowedDates = React.useMemo(
    () =>
      props.schedules
        ? props.schedules.map(schedule => schedule.date)
        : undefined,
    [props.schedules]
  );
  const selectedSchedule = React.useMemo(
    () =>
      props.schedules && date
        ? props.schedules.find(schedule => schedule.date === date)
        : undefined,
    [date, props.schedules]
  );
  const { currentMonthStartRef, lastMonthDate, currentDateRef } = useMonthRange(
    props.schedules
  );

  const selectedPackageDisplayName = selectedPackage
    ? selectedPackage.data.name.text
    : '';
  const whatsappMsg = `${l('Service')}: ${selectedPackageDisplayName}
${l('Date')}: ${date} ${time ? `(${time})` : ''}
${l(visitorType === 'foreigner' ? 'Foreigner' : 'Malaysian')}
${l('Adult')}: ${adultCount || 0}, ${l('Kid')}: ${kidCount || 0}
${l('Total')}: ${totalPrice}.${decimals}
`;

  return (
    <>
      <fieldset className="my-3">
        <legend className="leading-loose my-1">{l('Date')}</legend>
        <div className="max-w-2xl sm:flex">
          <DateField
            value={date}
            onChangeValue={newDate => {
              setDate(newDate);
              const newSchedule = props.schedules.find(
                schedule => schedule.date === newDate
              );
              if (newSchedule && newSchedule.time[0]) {
                setTime(newSchedule.time[0]);
              }
            }}
            allowedDates={allowedDates}
            minDate={currentDateRef.current}
            fromMonth={currentMonthStartRef.current}
            toMonth={lastMonthDate}
            required
          />
          {selectedSchedule && (
            <SelectField
              value={time}
              onChangeValue={setTime}
              options={selectedSchedule.time}
              aria-label={l('Time')}
            />
          )}
        </div>
      </fieldset>
      <fieldset>
        <div className="space-y-2 md:flex md:space-x-2 md:space-y-0">
          {displayedPackages.map(({ id, title, includes }) => (
            <button
              onClick={() => setPkg(id)}
              className={cx(
                'px-6 py-3 text-justify w-full md:flex-1 align-top inline-flex flex-col rounded-lg bg-white focus:outline-none',
                pkg === id
                  ? 'border-blue-500 border focus:shadow-outline-blue'
                  : 'border-gray-300 border'
              )}
              type="button"
              key={id}
            >
              <span
                className={cx(
                  'block text-lg xs:text-xl',
                  pkg === id && 'text-blue-800'
                )}
              >
                {title}
              </span>
              <span className="text-sm text-gray-500">
                {includes.join(l('itemSeparator'))}
              </span>
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="my-3">
        <legend className="leading-loose my-1">{l('Basic Info')}</legend>
        <div className="space-y-2 sm:flex sm:space-x-2 sm:space-y-0">
          <VisitorTypeSelect wrapperClassName="flex-1 sm:w-48" />
          <TextField
            type="number"
            step={1}
            min={0}
            value={adultCount}
            onChangeValue={setAdultCount}
            placeholder={l('pax')}
            append={l('Adult')}
            className="w-20 xs:w-auto"
            wrapperClassName="flex-1 sm:w-0"
          />
          <TextField
            type="number"
            step={1}
            min={0}
            value={kidCount}
            onChangeValue={setKidCount}
            placeholder={l('pax')}
            append={l('Kid')}
            className="w-20 xs:w-auto"
            wrapperClassName="flex-1 sm:w-0"
          />
        </div>
      </fieldset>
      <div className="xs:flex justify-between items-center pt-3 border-t border-gray-300 mt-4">
        <output className="block mb-2">
          <small className="text-2xl mr-1">{l('RM')}</small>
          <span className="text-4xl">{totalPrice}</span>
          <small className="text-2xl">.{decimals}</small>
        </output>
        <div className="text-right">
          <MessageBtn
            message={whatsappMsg}
            onClick={() => {
              trackEvent({
                category: 'button',
                action: 'click',
                label: `order ${selectedPackage?.uid}`,
                value: totalPrice,
              });
            }}
            disabled={!date || !pkg || (!adultCount && !kidCount)}
          />
        </div>
      </div>
    </>
  );
};

export interface PackageDetails {
  uid: string;
  data: {
    name: {
      text: string;
    };
    includedServices: Array<{
      service: {
        document: Array<{
          data: {
            name: {
              text: string;
            };
          };
        }>;
      };
    }>;
    additionalServices: Array<{
      text: {
        text: string;
      };
    }>;
    localAdultPrice: number;
    localKidPrice: number;
    foreignAdultPrice: number;
    foreignKidPrice: number;
    discounts: Array<{
      requiredUnit: number;
      localKidPrice: number;
      localAdultPrice: number;
      foreignKidPrice: number;
      foreignAdultPrice: number;
    }>;
  };
}
